import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { CalendarDays, Play, Plus, Tag, Users } from "lucide-react";
import {
  APPOINTMENT_TYPES,
  COLLECT_FAQS,
  INTEREST_AREAS,
  ORG_TYPES,
  RELATED,
  VIDEO_MEDIA,
  VIDEOS,
} from "../../constants/collect";
import { ArrowIcon, CornerMarks, Field, MediaSlot, SectionShell, useFade, useKnockoutLogo } from "./ui";

const INTENTS = [
  {
    id: "demo",
    title: "Request a live demo",
    copy: "Discover how intelligent automation can transform your organization's debt recovery operations.",
    cta: "Request my demo",
  },
  {
    id: "sales",
    title: "Talk to sales",
    copy: "Whether you're looking to modernize collections, improve portfolio performance or automate engagement, our specialists are here.",
    cta: "Talk to sales",
  },
  {
    id: "appointment",
    title: "Book a consultation",
    copy: "Schedule a personalized session on recovery strategy, workflow design, integration and reporting.",
    cta: "Book consultation",
  },
  {
    id: "pricing",
    title: "Request pricing",
    copy: "Share your portfolio profile and we'll prepare a commercial conversation around SimCollect.",
    cta: "Request pricing",
  },
];

const NEXT_STEPS = [
  {
    n: "01",
    title: "Talk to Sales",
    copy: "Speak with a recovery solutions specialist.",
    href: "#sales",
    icon: Users,
  },
  {
    n: "02",
    title: "Book a Consultation",
    copy: "Design workflows, payments and reporting together.",
    href: "#appointment",
    icon: CalendarDays,
  },
  {
    n: "03",
    title: "Request Pricing",
    copy: "Start a commercial conversation for your book.",
    href: "#pricing",
    icon: Tag,
  },
  {
    n: "04",
    title: "Watch Product Demo",
    copy: "See portfolio upload, workspace and dashboards.",
    href: "#videos",
    icon: Play,
  },
];

const INITIAL = {
  name: "",
  company: "",
  title: "",
  institution: "",
  email: "",
  phone: "",
  date: "",
  appointment: APPOINTMENT_TYPES[0],
};

function CollectClose() {
  const { fade, reduce } = useFade();
  const [video, setVideo] = useState(0);
  const [openFaq, setOpenFaq] = useState(COLLECT_FAQS[0]?.n || "");
  const [intent, setIntent] = useState("demo");
  const [form, setForm] = useState(INITIAL);
  const [interests, setInterests] = useState([]);
  const [sent, setSent] = useState(false);
  const activeVideo = VIDEOS[video];
  const activeIntent = INTENTS.find((item) => item.id === intent) || INTENTS[0];

  useEffect(() => {
    const apply = () => {
      const id = window.location.hash.replace("#", "");
      if (id === "sales" || id === "appointment" || id === "demo" || id === "pricing") setIntent(id);
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (sent) setSent(false);
  };

  const toggleInterest = (item) => {
    setInterests((current) =>
      current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]
    );
    if (sent) setSent(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSent(true);
  };

  return (
    <>
      <SectionShell id="videos" labelledBy="videos-heading" tone="paper">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Watch SimCollect in action
            </p>
            <h2
              id="videos-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Product demonstrations
              <span className="mt-1 block">&amp; video tutorials</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Learn how SimCollect transforms debt recovery through intelligent automation and
              customer engagement.
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="relative h-full min-h-[22rem]">
              <CornerMarks />
              <div className="relative h-full min-h-[22rem] overflow-hidden border border-navy/10 bg-navy">
                <MediaSlot
                  src={VIDEO_MEDIA.src}
                  alt={VIDEO_MEDIA.alt}
                  label={activeVideo.title}
                  spec={VIDEO_MEDIA.spec}
                  className="absolute inset-0 h-full w-full"
                  tone="dark"
                />
                <div className="pointer-events-none absolute inset-0 bg-navy/55" aria-hidden="true" />
                <button
                  type="button"
                  className="absolute inset-0 z-[2] flex cursor-pointer flex-col items-center justify-center gap-3 px-6 text-white transition hover:bg-collect-red/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold"
                  aria-label={`Play ${activeVideo.title}`}
                >
                  <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-collect-gold text-navy shadow-[0_16px_32px_-10px_rgba(229,184,11,0.95)]">
                    <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" strokeWidth={0} aria-hidden="true" />
                  </span>
                  <span className="text-center text-sm font-semibold tracking-tight">{activeVideo.title}</span>
                </button>
              </div>
            </div>

            <ol className="flex flex-col border border-navy/10 bg-white">
              {VIDEOS.map((item, index) => {
                const current = index === video;
                return (
                  <li key={item.id} className="border-t border-navy/8 first:border-t-0">
                    <button
                      type="button"
                      onClick={() => setVideo(index)}
                      className={`flex min-h-14 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold ${
                        current ? "border-l-[3px] border-collect-red bg-[#fff4f4]" : "border-l-[3px] border-transparent hover:bg-[#faf6f4]"
                      }`}
                    >
                      <span className={`font-mono text-[11px] tracking-[0.14em] ${current ? "text-collect-red" : "text-navy/35"}`}>
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold tracking-tight text-navy">{item.title}</span>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy/40">
                          {item.kind}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="faqs" labelledBy="faqs-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <motion.header
              className="max-w-3xl"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                Frequently asked questions
              </p>
              <h2
                id="faqs-heading"
                className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
              >
                SimCollect, answered
              </h2>
            </motion.header>
            <Link
              to="/faqs#topic-simcollect"
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-collect-red transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold"
            >
              Complete FAQ page
              <ArrowIcon className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="mt-10 border border-navy/10">
            {COLLECT_FAQS.map((item) => {
              const isOpen = openFaq === item.n;
              const panelId = `collect-faq-${item.n}`;
              return (
                <div key={item.id} className="border-t border-navy/10 first:border-t-0">
                  <h3>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaq(isOpen ? "" : item.n)}
                      className="flex min-h-16 w-full cursor-pointer items-start gap-4 px-5 py-4 text-left transition hover:bg-[#faf6f4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold sm:px-6"
                    >
                      <span className="mt-1 font-mono text-[11px] tracking-[0.16em] text-collect-red">
                        {item.n}
                      </span>
                      <span className="min-w-0 flex-1 pt-0.5 text-base font-semibold leading-snug tracking-tight text-navy">
                        {item.question}
                      </span>
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border transition ${
                          isOpen
                            ? "border-collect-gold bg-collect-gold text-navy"
                            : "border-navy/15 bg-white text-navy"
                        }`}
                      >
                        <Plus
                          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-45" : ""}`}
                          strokeWidth={2}
                        />
                      </span>
                    </button>
                  </h3>
                  <motion.div
                    id={panelId}
                    role="region"
                    initial={false}
                    animate={
                      reduce
                        ? { height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }
                        : isOpen
                          ? { height: "auto", opacity: 1 }
                          : { height: 0, opacity: 0 }
                    }
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-6 pl-[4.5rem] text-sm leading-relaxed text-navy/60 sm:px-6 sm:pl-[5.75rem]">
                      {item.answer ? <p>{item.answer}</p> : null}
                      {item.bullets ? (
                        <ul className="mt-3 list-disc space-y-1 pl-5">
                          {item.bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="demo" labelledBy="demo-heading" tone="paper">
        <div id="sales" className="scroll-mt-32" />
        <div id="appointment" className="scroll-mt-32" />
        <div id="pricing" className="scroll-mt-32" />
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Get started
            </p>
            <h2
              id="demo-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Experience SimCollect
              <span className="mt-1 block text-collect-red">in action</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              {activeIntent.copy}
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="flex h-full flex-col bg-white p-6 sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/40">
                How should we help?
              </p>
              <ul className="mt-4 flex flex-1 flex-col gap-2">
                {INTENTS.map((item, index) => {
                  const selected = item.id === intent;
                  return (
                    <li key={item.id} className="flex min-h-0 flex-1">
                      <button
                        type="button"
                        onClick={() => setIntent(item.id)}
                        className={`flex h-full min-h-16 w-full cursor-pointer flex-col items-start justify-center border px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold ${
                          selected
                            ? "border-collect-red bg-collect-red text-white"
                            : "border-navy/10 bg-[#faf6f4] text-navy hover:border-collect-red/40"
                        }`}
                      >
                        <span className={`font-mono text-[11px] tracking-[0.16em] ${selected ? "text-white/70" : "text-collect-red"}`}>
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="mt-2 text-sm font-semibold tracking-tight">{item.title}</span>
                        <span className={`mt-1 text-xs leading-relaxed ${selected ? "text-white/75" : "text-navy/50"}`}>
                          {item.copy}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {intent === "sales" ? (
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  {["Call sales", "Email sales", "WhatsApp Business", "Live chat"].map((item) => (
                    <li
                      key={item}
                      className="border border-navy/10 bg-[#faf6f4] px-3 py-3 text-sm font-semibold text-navy/80"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-6 border-t border-navy/10 pt-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-collect-red">
                  Typical reply
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-navy/55">
                  A SimCollect specialist usually responds within one business day.
                </p>
              </div>
            </div>

            <form className="flex h-full flex-col bg-[#faf6f4] px-6 py-8 sm:px-8 sm:py-10" onSubmit={onSubmit} noValidate>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Full name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  autoComplete="name"
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  required
                  autoComplete="organization"
                />
                <Field
                  label="Position"
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  autoComplete="organization-title"
                />
                <div>
                  <label
                    htmlFor="institution"
                    className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
                  >
                    Organization type <span className="text-collect-red">*</span>
                  </label>
                  <select
                    id="institution"
                    name="institution"
                    value={form.institution}
                    onChange={onChange}
                    required
                    className="mt-2 min-h-12 w-full cursor-pointer border border-navy/15 bg-white px-4 text-sm text-navy outline-none transition focus:border-collect-gold focus:ring-2 focus:ring-collect-gold/40"
                  >
                    <option value="" disabled>
                      Select organization type
                    </option>
                    {ORG_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <Field
                  label="Email address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  autoComplete="email"
                />
                <Field
                  label="Phone number"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  autoComplete="tel"
                />
                <Field label="Preferred demo date" name="date" type="date" value={form.date} onChange={onChange} />
                {intent === "appointment" ? (
                  <div>
                    <label
                      htmlFor="appointment"
                      className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
                    >
                      Consultation focus
                    </label>
                    <select
                      id="appointment"
                      name="appointment"
                      value={form.appointment}
                      onChange={onChange}
                      className="mt-2 min-h-12 w-full cursor-pointer border border-navy/15 bg-white px-4 text-sm text-navy outline-none transition focus:border-collect-gold focus:ring-2 focus:ring-collect-gold/40"
                    >
                      {APPOINTMENT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div />
                )}
                <div className="sm:col-span-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40">
                    Areas of interest
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {INTEREST_AREAS.map((item) => {
                      const on = interests.includes(item);
                      return (
                        <li key={item}>
                          <button
                            type="button"
                            onClick={() => toggleInterest(item)}
                            aria-pressed={on}
                            className={`inline-flex min-h-10 cursor-pointer items-center rounded-full px-3 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold ${
                              on
                                ? "bg-collect-red text-white"
                                : "border border-navy/15 bg-white text-navy/70 hover:border-navy/40"
                            }`}
                          >
                            {item}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="mt-auto pt-8">
                <button
                  type="submit"
                  className="inline-flex min-h-12 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-collect-red px-6 text-sm font-semibold text-white transition hover:bg-[#dc3333] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-red focus-visible:ring-offset-2"
                >
                  {activeIntent.cta}
                  <ArrowIcon />
                </button>
                {sent ? (
                  <p className="mt-4 text-sm text-navy/55" role="status">
                    Details captured. A SimCollect specialist will follow up to confirm your request.
                  </p>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="related" labelledBy="related-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Related solutions
            </p>
            <h2
              id="related-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Recovery works best
              <span className="mt-1 block">with acquisition and trust</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Debt recovery is most effective when combined with intelligent customer acquisition
              and trusted lending decisions. Explore the complete SimFinance ecosystem.
            </p>
          </motion.header>

          <ul className="mt-12 grid gap-6 lg:grid-cols-2">
            {RELATED.map((item) => (
              <li key={item.id} className="relative overflow-hidden border border-navy/10 bg-[#faf6f4]">
                <RelatedCard item={item} />
              </li>
            ))}
          </ul>
        </div>
      </SectionShell>

      <SectionShell id="ready" labelledBy="ready-heading" tone="red">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-collect-gold to-transparent"
            aria-hidden="true"
          />
          <p
            className="pointer-events-none absolute -left-2 top-10 select-none text-[6rem] font-black leading-none tracking-tight text-white/[0.1] sm:text-[9rem] lg:top-6 lg:text-[11rem]"
            aria-hidden="true"
          >
            RECOVER
          </p>

          <motion.div
            className="relative border border-white/20 bg-white/[0.08] p-6 sm:p-10 lg:p-12"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <CornerMarks dark />
            <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-14">
              <div className="flex flex-col">
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-collect-gold">
                  Final call
                </p>
                <h2
                  id="ready-heading"
                  className="mt-4 font-semibold leading-[1.08] tracking-tight [font-size:clamp(2rem,5vw,3.75rem)]"
                >
                  Protect your portfolio.
                  <span className="mt-1 block">Strengthen customer relationships.</span>
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
                  Effective debt recovery is about more than collecting outstanding balances—it&apos;s
                  about preserving trust, improving operational efficiency and supporting long-term
                  financial sustainability.
                </p>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75">
                  With SimCollect, your organization gains an intelligent digital platform that
                  automates collections, enhances customer engagement and delivers the visibility
                  needed to make smarter recovery decisions.
                </p>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75">
                  Whether you&apos;re a bank, FinTech, insurer or any organization managing
                  receivables, SimCollect helps you recover more, operate more efficiently and
                  build stronger customer relationships.
                </p>
                <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row sm:flex-wrap">
                  <a
                    href="#demo"
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-collect-gold px-7 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    Request a Demo
                  </a>
                  <a
                    href="#sales"
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-white px-7 text-sm font-semibold text-navy transition hover:bg-collect-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    Talk to Sales
                  </a>
                </div>
              </div>

              <ol className="flex flex-col overflow-hidden border border-white/20 bg-navy/20" aria-label="More ways to get started">
                {NEXT_STEPS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.n} className="border-t border-white/10 first:border-t-0">
                      <a
                        href={item.href}
                        className="group flex min-h-[5.5rem] cursor-pointer items-start gap-4 px-5 py-5 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold"
                      >
                        <span className="font-mono text-[11px] tracking-[0.18em] text-collect-gold">
                          {item.n}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 text-sm font-semibold tracking-tight text-white">
                            <Icon className="h-4 w-4 shrink-0 text-collect-gold" strokeWidth={1.75} aria-hidden="true" />
                            {item.title}
                          </span>
                          <span className="mt-1 block text-sm leading-relaxed text-white/70">{item.copy}</span>
                        </span>
                        <ArrowIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-collect-gold transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </motion.div>
        </div>
      </SectionShell>
    </>
  );
}

function RelatedCard({ item }) {
  const { href: logoHref, failed } = useKnockoutLogo(item.logo);
  const logo = !failed && logoHref ? logoHref : item.logo;
  const destination = item.href;
  const interactive = Boolean(destination);
  const isExternal = Boolean(destination?.startsWith("http"));

  const inner = (
    <>
      <div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-collect-red">{item.index}</p>
        {logo ? (
          <img src={logo} alt="" className="mt-4 h-10 w-auto max-w-[12rem] object-contain object-left" />
        ) : null}
        <h3 className="mt-4 text-2xl font-semibold tracking-tight text-navy">{item.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-navy/60 sm:text-base">{item.copy}</p>
      </div>
      <span className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold text-collect-red ${interactive ? "group-hover:text-navy" : ""}`}>
        {item.cta}
        {interactive ? (
          <ArrowIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        ) : null}
      </span>
    </>
  );

  const className = interactive
    ? "group flex h-full min-h-[16rem] cursor-pointer flex-col justify-between p-6 transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold sm:p-8"
    : "flex h-full min-h-[16rem] flex-col justify-between p-6 sm:p-8";

  if (!interactive) {
    return <div className={className}>{inner}</div>;
  }

  if (isExternal) {
    return (
      <a href={destination} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  }

  return (
    <Link to={destination} className={className}>
      {inner}
    </Link>
  );
}

export default CollectClose;
